import { Pencil, CheckCircle2 } from 'lucide-react';
import { LABEL_CLS } from '../../styles/tokens';
import { isStabilized } from '../../lib/stabilization';
import type { WizardDraft, StepId } from './state';

interface Props {
  draft: WizardDraft;
  steps: StepId[];
  /** Jump to a step for inline edit. */
  onJump: (step: StepId) => void;
}

/** Final review surface. Recap-card per step with an edit pencil that
 *  jumps the wizard back. Sticky submit lives in the parent. */
export function ReviewStep({ draft, steps, onJump }: Props) {
  const stab = isStabilized(draft.field_param_readings);
  const samplesCollected = draft.samples_collected.filter((s) => s.collected).length;
  const samplesTotal = draft.samples_collected.length;

  return (
    <div className="space-y-3">
      <header>
        <h2 className="text-lg font-semibold text-mono-100">Review &amp; submit</h2>
        <p className="text-xs text-mono-400 mt-0.5">Last chance to fix anything before the record is queued.</p>
      </header>

      <Card title="Gauging" onEdit={() => onJump('gauging')}>
        {draft.flag_code ? (
          <p className="text-amber-300 text-sm">Flagged: <span className="font-mono">{draft.flag_code}</span> · no measurement recorded</p>
        ) : (
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <Field label="DTW (ft)" value={draft.depth_to_water_ft} />
            <Field label="Water elev (ft)" value={draft.water_elevation_ft} />
            <Field label="Product (ft)" value={draft.product_thickness_ft} />
          </dl>
        )}
        {draft.is_dry && <p className="text-amber-300 text-xs mt-2">Well is dry.</p>}
      </Card>

      {steps.includes('field-params') && (
        <Card
          title={`Field parameters ${draft.field_param_readings.length > 0 ? `· ${draft.field_param_readings.length} readings` : ''}`}
          onEdit={() => onJump('field-params')}
        >
          {draft.field_param_readings.length === 0 ? (
            <p className="text-mono-400 text-sm">No readings captured.</p>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              {stab.stabilized ? (
                <span className="inline-flex items-center gap-1 text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" /> Stabilized
                </span>
              ) : (
                <span className="text-amber-300">Not stabilized · {stab.consecutive}/3</span>
              )}
              <span className="text-mono-400">— latest captured</span>
            </div>
          )}
        </Card>
      )}

      {steps.includes('purge') && (
        <Card title="Purge" onEdit={() => onJump('purge')}>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <Field label="Method" value={draft.purge_method} />
            <Field label="Volume (L)" value={draft.purge_volume_l} />
            <Field label="Rate (L/min)" value={draft.pump_rate_lpm} />
          </dl>
        </Card>
      )}

      {steps.includes('samples') && (
        <Card title={`Samples · ${samplesCollected}/${samplesTotal}`} onEdit={() => onJump('samples')}>
          {samplesTotal === 0 ? (
            <p className="text-mono-400 text-sm">No containers configured.</p>
          ) : samplesCollected === 0 ? (
            <p className="text-amber-300 text-sm">No containers checked off yet.</p>
          ) : (
            <ul className="space-y-0.5 text-sm text-mono-300">
              {draft.samples_collected.filter((s) => s.collected).map((s, i) => (
                <li key={i} className="truncate">
                  <span className="text-emerald-400">✓</span>{' '}
                  {s.analyte_group} — {s.container}{s.preservative ? ` · ${s.preservative}` : ''}{s.filtered ? ' · filtered' : ''}{s.lab_id ? ` · ${s.lab_id}` : ''}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card title="Observations" onEdit={() => onJump('observations')}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          <Field label="Weather" value={draft.weather} />
          <Field label="Well condition" value={draft.well_condition} />
          <Field label="Odor" value={draft.odor} />
          <Field label="Sheen" value={draft.sheen} />
        </dl>
        {draft.observations && <p className="text-sm text-mono-300 mt-2 whitespace-pre-wrap">{draft.observations}</p>}
        {draft.notes && <p className="text-sm text-mono-200 mt-2 italic">"{draft.notes}"</p>}
      </Card>
    </div>
  );
}

function Card({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-mono-800 bg-mono-900/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] tracking-widest uppercase text-mono-400">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 active:text-cyan-500"
          aria-label={`Edit ${title}`}
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
          edit
        </button>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div className="min-w-0">
      <dt className={LABEL_CLS}>{label}</dt>
      <dd className="text-mono-200 truncate">{value && value.toString().trim() ? value : <span className="text-mono-500">—</span>}</dd>
    </div>
  );
}
